import concat from "lodash/concat";
import keyBy from "lodash/keyBy";
import { TemplateKey } from "PFApp/constants/templates";
import { SuspendedPill } from "PFComponents/suspended_pill";
import { getProfileName } from "PFCore/helpers/profile";
import { useDateFormatter } from "PFCore/hooks";
import { BasicProfile } from "PFTypes";
import { RecentSearch } from "PFTypes/recent_searches";
import { useTranslation } from "react-i18next";

import { EntityConfig, SearchConfig, SearchEntity } from "../search_autocomplete.types";
import { useAutocompleteEntityOptions } from "./use_autocomplete_entity_options";
import { useAutocompleteRecentSearchesOptions } from "./use_autocomplete_recent_searches_options";

type UseAutocompleteOptions = {
  term: string;
  config: SearchConfig;
  recentSearches: RecentSearch[];
  queryEnabled?: boolean;
};

export const useAutocompleteOptions = ({
  term,
  config,
  recentSearches,
  queryEnabled
}: UseAutocompleteOptions) => {
  const { t } = useTranslation(["activities", "translation"]);
  const { formatDate } = useDateFormatter();

  const configs = keyBy<EntityConfig>(config, "entity");

  const profilesOptions = useAutocompleteEntityOptions<BasicProfile>({
    term,
    config: configs[SearchEntity.Profile],
    iconName: "profile",
    label: t("translation:profile"),
    enabled: queryEnabled,
    getRedirectUrl: ({ id }) => `/profiles/${id}`,
    getEntityTitle: (entity) => getProfileName(entity),
    getEntitySubTitle: ({ email }) => email,
    getEntityAdditionalData: ({ suspendedAt }) => {
      if (!suspendedAt) {
        return null;
      }
      const ariaLabel = t("translation:suspendedDate", { date: formatDate(suspendedAt) });
      return {
        ariaLabel: ariaLabel,
        displayElement: <SuspendedPill suspendedAt={suspendedAt} />
      };
    }
  });
  const rolesOptions = useAutocompleteEntityOptions({
    term,
    config: configs[SearchEntity.Role],
    templateKey: TemplateKey.Role,
    iconName: "role",
    label: t("activities:templates.role"),
    enabled: queryEnabled,
    getRedirectUrl: ({ id }) => `/activities/${id}`
  });
  const auditRolesOptions = useAutocompleteEntityOptions({
    term,
    config: configs[SearchEntity.AuditRole],
    templateKey: TemplateKey.AuditRole,
    iconName: "role-audit",
    label: t("activities:templates.audit_role"),
    enabled: queryEnabled,
    getRedirectUrl: ({ id }) => `/activities/${id}`
  });
  const engagementsOptions = useAutocompleteEntityOptions({
    term,
    config: configs[SearchEntity.Engagement],
    templateKey: TemplateKey.Engagement,
    iconName: "engagement",
    label: t("activities:templates.engagement"),
    enabled: queryEnabled,
    getRedirectUrl: ({ id }) => `/activities/${id}`
  });
  const auditEngagementsOptions = useAutocompleteEntityOptions({
    term,
    config: configs[SearchEntity.AuditEngagement],
    templateKey: TemplateKey.AuditEngagement,
    iconName: "engagement-audit",
    label: t("activities:templates.audit_engagement"),
    enabled: queryEnabled,
    getRedirectUrl: ({ id }) => `/activities/${id}`
  });

  const recentSearchesOptions = useAutocompleteRecentSearchesOptions({ recentSearches });

  return concat(
    profilesOptions,
    engagementsOptions,
    rolesOptions,
    auditRolesOptions,
    auditEngagementsOptions,
    recentSearchesOptions
  );
};
